import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';

import { OrganiseDocumentTag, RecordingState } from '@karehero/models';
import { formatFileSize } from '@karehero/helpers/files';
import { formatDuration } from '@karehero/helpers/datetime';

import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { Icon } from '@/components/atoms/Icon/Icon';
import { getDocumentTagColor } from '@/theme/mui/color';

export interface AudioRecordData {
  title: string;
  description?: string;
  file: File;
  tags: string[];
}

export interface AudioRecordPageProps {
  recordingState: RecordingState;
  durationSeconds: number;
  maxDurationSeconds?: number;
  recordingError: string | null;
  recordedFile: File | null;
  availableTags: OrganiseDocumentTag[];
  isUploading?: boolean;
  onBack?: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onResetRecording: () => void;
  onUpload: (data: AudioRecordData) => void;
}

/* ── Sub-components ─────────────────────────────────────────────── */

const RecorderPanel = ({
  recordingState,
  durationSeconds,
  maxDurationSeconds,
  recordingError,
  onStartRecording,
  onStopRecording,
}: {
  recordingState: 'idle' | 'recording';
  durationSeconds: number;
  maxDurationSeconds?: number;
  recordingError: string | null;
  onStartRecording: () => void;
  onStopRecording: () => void;
}) => {
  const theme = useTheme();
  const isRecording = recordingState === 'recording';

  return (
    <Stack
      spacing={3}
      sx={{
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: 160,
          height: 160,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isRecording
            ? alpha(theme.palette.error.main, 0.1)
            : alpha(theme.palette.primary.main, 0.08),
          transition: 'background-color 0.3s ease',
        }}
      >
        <Icon
          icon='microphone'
          fontSize={56}
          color={
            isRecording
              ? theme.palette.error.main
              : theme.palette.text.secondary
          }
        />
      </Box>

      <Typography
        variant='h4'
        color={isRecording ? 'error.main' : 'text.primary'}
        sx={{
          fontWeight: 600,
          fontFamily: 'monospace',
        }}
      >
        {formatDuration(durationSeconds)}
      </Typography>

      {maxDurationSeconds && (
        <Typography
          variant='caption'
          sx={{
            color: 'text.secondary',
          }}
        >
          {isRecording
            ? `${formatDuration(maxDurationSeconds - durationSeconds)} remaining`
            : `Max ${formatDuration(maxDurationSeconds)}`}
        </Typography>
      )}

      {isRecording ? (
        <Button
          variant='contained'
          color='error'
          size='large'
          startIcon={<Icon icon='stop' fontSize={18} />}
          onClick={onStopRecording}
        >
          Stop Recording
        </Button>
      ) : (
        <Button
          variant='contained'
          size='large'
          startIcon={<Icon icon='microphone' fontSize={18} />}
          onClick={onStartRecording}
        >
          Start Recording
        </Button>
      )}

      {recordingError && (
        <Typography
          variant='body2'
          color='error'
          sx={{
            textAlign: 'center',
          }}
        >
          {recordingError}
        </Typography>
      )}
    </Stack>
  );
};

const MetadataForm = ({
  recordedFile,
  durationSeconds,
  playbackUrl,
  availableTags,
  isUploading,
  onReRecord,
  onSubmit,
}: {
  recordedFile: File;
  durationSeconds: number;
  playbackUrl: string | null;
  availableTags: OrganiseDocumentTag[];
  isUploading?: boolean;
  onReRecord: () => void;
  onSubmit: (data: AudioRecordData) => void;
}) => {
  const theme = useTheme();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [titleError, setTitleError] = useState<string | null>(null);

  const handleTagToggle = useCallback((tagId: string) => {
    setSelectedTags((current) =>
      current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId],
    );
  }, []);

  const handleSubmit = useCallback(() => {
    setTitleError(null);

    if (!title.trim()) {
      setTitleError('Title is required');
      return;
    }

    onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      file: recordedFile,
      tags: selectedTags,
    });
  }, [title, description, recordedFile, selectedTags, onSubmit]);

  const canSubmit = title.trim() && !isUploading;

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          p: 2,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'primary.main',
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <Stack
          direction='row'
          spacing={1.5}
          sx={{
            alignItems: 'center',
          }}
        >
          <Icon
            icon='microphone'
            fontSize={28}
            color={theme.palette.primary.main}
          />
          <Stack
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              variant='body1'
              noWrap
              sx={{
                fontWeight: 500,
              }}
            >
              {recordedFile.name}
            </Typography>
            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
              }}
            >
              {formatDuration(durationSeconds)} &bull;{' '}
              {formatFileSize(recordedFile.size)}
            </Typography>
          </Stack>
          {!isUploading && (
            <Button size='small' onClick={onReRecord}>
              Re-record
            </Button>
          )}
        </Stack>
      </Box>

      {playbackUrl && (
        <Box
          component='audio'
          controls
          src={playbackUrl}
          sx={{ width: '100%', height: 40 }}
        />
      )}

      <TextField
        label='Title'
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        error={!!titleError}
        helperText={titleError}
        required
        fullWidth
        disabled={isUploading}
        autoFocus
        placeholder='e.g. Care meeting notes'
      />

      <TextField
        label='Description'
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        rows={3}
        fullWidth
        disabled={isUploading}
        placeholder='Optional notes about this recording'
      />

      {availableTags.length > 0 && (
        <Stack spacing={1}>
          <Typography
            variant='subtitle2'
            sx={{
              color: 'text.secondary',
            }}
          >
            Tags (optional)
          </Typography>
          <Stack
            direction='row'
            spacing={1}
            useFlexGap
            sx={{
              flexWrap: 'wrap',
            }}
          >
            {availableTags.map((tag) => {
              const tagColor = getDocumentTagColor(tag.id);
              const isSelected = selectedTags.includes(tag.id);
              return (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  onClick={() => handleTagToggle(tag.id)}
                  disabled={isUploading}
                  variant={isSelected ? 'filled' : 'outlined'}
                  sx={{
                    borderColor: tagColor,
                    color: isSelected ? 'white' : tagColor,
                    bgcolor: isSelected ? tagColor : 'transparent',
                    '&:hover': {
                      bgcolor: isSelected ? tagColor : `${tagColor}20`,
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      )}

      <Button
        variant='contained'
        onClick={handleSubmit}
        disabled={!canSubmit}
        fullWidth
        size='large'
        startIcon={
          isUploading ? (
            <CircularProgress size={18} color='inherit' />
          ) : (
            <Icon icon='upload' fontSize={18} />
          )
        }
      >
        {isUploading ? 'Uploading...' : 'Upload Recording'}
      </Button>
    </Stack>
  );
};

/* ── Main page ──────────────────────────────────────────────────── */

export const AudioRecordPage = ({
  recordingState,
  durationSeconds,
  maxDurationSeconds,
  recordingError,
  recordedFile,
  availableTags,
  isUploading,
  onBack,
  onStartRecording,
  onStopRecording,
  onResetRecording,
  onUpload,
}: AudioRecordPageProps) => {
  const showRecorder =
    recordingState === 'idle' || recordingState === 'recording';
  const showForm = recordingState === 'stopped' && recordedFile;

  const playbackUrl = useMemo(
    () => (recordedFile ? URL.createObjectURL(recordedFile) : null),
    [recordedFile],
  );

  useEffect(() => {
    return () => {
      if (playbackUrl) URL.revokeObjectURL(playbackUrl);
    };
  }, [playbackUrl]);

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={
          <PageHeader
            title={showForm ? 'Recording Details' : 'Record Audio'}
            description={
              showForm
                ? 'Add details for your recording'
                : 'Record an audio note for your documents'
            }
          />
        }
        contentSlot={
          <Stack spacing={3}>
            {showRecorder && (
              <RecorderPanel
                recordingState={recordingState as 'idle' | 'recording'}
                durationSeconds={durationSeconds}
                maxDurationSeconds={maxDurationSeconds}
                recordingError={recordingError}
                onStartRecording={onStartRecording}
                onStopRecording={onStopRecording}
              />
            )}
            {showForm && (
              <MetadataForm
                recordedFile={recordedFile}
                durationSeconds={durationSeconds}
                playbackUrl={playbackUrl}
                availableTags={availableTags}
                isUploading={isUploading}
                onReRecord={onResetRecording}
                onSubmit={onUpload}
              />
            )}
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

AudioRecordPage.displayName = 'AudioRecordPage';
export default AudioRecordPage;
